import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNetwork } from '../../../../utils/network';
import {
  RecoveryCodesGenerateDialog,
  type RecoveryCodesStatus
} from '../../TwoFactorAuth/RecoveryCodesGenerateDialog';
import { PT, panelSx, primaryBtnSx } from '../../../../components/portalUi';

/* Settings → Security: how many recovery codes are left, and a new set on demand.
 * A new set replaces the old one on the server, which asks for the password — and,
 * with 2FA on, a current authenticator code — so a session left open on someone
 * else's screen cannot mint codes. The dialog itself (RecoveryCodesGenerateDialog) is
 * shared with the save-your-codes prompt on arrival; the new codes are shown once and
 * it closes only through Done. */
export const SettingsRecoveryCodes = () => {
  const { get } = useNetwork();
  const [status, setStatus] = useState<RecoveryCodesStatus | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    get('/user/otp/recovery-codes/status', true)
      .then((result) => {
        if (cancelled) return;
        setStatus({
          remaining: typeof result?.remaining === 'number' ? result.remaining : 0,
          generatedAt: result?.generatedAt ?? null
        });
      })
      .catch(() => {
        if (!cancelled) setStatusFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const low = status !== null && (status.generatedAt === null || status.remaining <= 2);
  const statusText = statusFailed
    ? 'Couldn’t check right now'
    : status === null
    ? 'Checking…'
    : status.generatedAt === null
    ? 'Not generated yet'
    : `${status.remaining} of 10 left`;

  return (
    <Box sx={{ ...panelSx, p: { xs: 2, sm: 2.5 }, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: PT.text }}>
            Recovery codes
          </Typography>
          <Typography
            sx={{ mt: 0.25, fontWeight: 700, fontSize: 15, color: low ? PT.gold : PT.tealText }}
          >
            {statusText}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 13.5, color: PT.textMuted, lineHeight: 1.5 }}>
            If you can&apos;t use your authenticator app, each code signs you in once.
            Generating new codes replaces your old set: the old codes stop working.
          </Typography>
        </Box>
        <Button
          onClick={() => setOpen(true)}
          sx={{ ...primaryBtnSx, px: 2.5, py: 1, flexShrink: 0 }}
        >
          Generate new codes
        </Button>
      </Box>

      <RecoveryCodesGenerateDialog
        open={open}
        onClose={() => setOpen(false)}
        onGenerated={(fresh) => {
          setStatus(fresh);
          setStatusFailed(false);
        }}
      />
    </Box>
  );
};
